import * as IoC from 'typescript-ioc';

import { IApiClient } from '../../infrastructure/api-client';
import * as SCHEDULED_EMAILS_API from '../../constants/api/scheduled-emails-api';
import {
    ICreateScheduledEmailRequestParams,
    IDeleteScheduledEmailRequestParams,
    IPreviewScheduledEmailRequestParams,
    IScheduledEmailDAO,
    IScheduledEmailDTO,
    IScheduledEmailRequestParams,
    IScheduledEmailsListDTO
} from '../scheduled-email.dao';
import { RetypePartial } from '../../infrastructure/type-extensions';
import { IApiDateConverter } from '../../infrastructure/api-date-converter';
import { IEventRequestParams } from '../shared.dto';

type IScheduledEmailDTOInternal = RetypePartial<IScheduledEmailDTO, 'dateTime', string>;

class ScheduledEmailDAO extends IScheduledEmailDAO {
    constructor(
        @IoC.Inject private readonly apiClient: IApiClient,
        @IoC.Inject private readonly apiDateConverter: IApiDateConverter
    ) {
        super();
    }

    public async getScheduledEmails(params: IEventRequestParams): Promise<IScheduledEmailsListDTO> {
        const response = await this.apiClient.get<IScheduledEmailDTOInternal[]>(SCHEDULED_EMAILS_API.SCHEDULED_EMAILS_URL, params);
        return response.parsedResponse.map(x => this.mapInternalScheduledEmailDTOToPublicDTO(x));
    }

    public async createScheduledEmail(params: ICreateScheduledEmailRequestParams): Promise<IScheduledEmailDTO> {
        const response = await this.apiClient.post<IScheduledEmailDTOInternal>(SCHEDULED_EMAILS_API.SCHEDULED_EMAILS_URL, {
            ...params,
            dateTime: this.apiDateConverter.dateToAPIString(params.dateTime)
        });
        return this.mapInternalScheduledEmailDTOToPublicDTO(response.parsedResponse);
    }

    public async updateScheduledEmail(params: IScheduledEmailRequestParams): Promise<IScheduledEmailDTO> {
        const response = await this.apiClient.put<IScheduledEmailDTOInternal>(SCHEDULED_EMAILS_API.MUTATE_SCHEDULED_EMAIL_URL, {
            ...params,
            dateTime: this.apiDateConverter.dateToAPIString(params.dateTime)
        });
        return this.mapInternalScheduledEmailDTOToPublicDTO(response.parsedResponse);
    }

    public async deleteScheduledEmail(params: IDeleteScheduledEmailRequestParams): Promise<void> {
        await this.apiClient.delete<void>(SCHEDULED_EMAILS_API.MUTATE_SCHEDULED_EMAIL_URL, params);
    }

    public async sendPreviewScheduledEmail(params: IPreviewScheduledEmailRequestParams): Promise<void> {
        const { meetingId, ...rest } = params;
        const url = !meetingId ? SCHEDULED_EMAILS_API.SEND_EMAIL_PREVIEW : SCHEDULED_EMAILS_API.SEND_EMAIL_MEETING_PREVIEW;
        const sendParams = !meetingId ? rest : params;
        await this.apiClient.get<void>(url, sendParams);
    }

    private mapInternalScheduledEmailDTOToPublicDTO(internalScheduledEmailDTO: IScheduledEmailDTOInternal) {
        return {
            ...internalScheduledEmailDTO,
            dateTime: this.apiDateConverter.apiStringToDate(internalScheduledEmailDTO.dateTime),
        };
    }
}

export { ScheduledEmailDAO };
